/**
 * OnboardingWizard
 *
 * Shows ONLY when profile.isOnboarded === false (DB-driven gate).
 * Persists all collected data to Supabase:
 *   - profiles   → firstName, lastName, birthday, avatarUrl, is_onboarded
 *   - user_settings → goal, yearType, fiscalStartMonth, ai, notifications
 *
 * Design: refined minimalism — generous space, clean type, surgical motion.
 */

import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, ChevronLeft, Check,
  Camera, Loader2, Calendar, Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useOnboardingMutations, OnboardingPayload } from "@/hooks/useOnboardingMutations";
import { useApp } from "@/contexts/AppContext";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function monthOptions(locale: string) {
  return Array.from({ length: 12 }, (_, i) => {
    const value = String(i + 1).padStart(2, "0");
    const label = new Intl.DateTimeFormat(locale, { month: "long" }).format(
      new Date(2024, i, 1),
    );
    return { label, value };
  });
}

const GOAL_SLIDER_KEYS = [
  "onboarding.goal.noPresence",
  "onboarding.goal.minimal",
  "onboarding.goal.lightHybrid",
  "onboarding.goal.balanced",
  "onboarding.goal.highPresence",
  "onboarding.goal.fullPresence",
] as const;

// ─────────────────────────────────────────────
// SLIDE VARIANTS
// ─────────────────────────────────────────────

const slide = {
  enter: (dir: number) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir < 0 ? 40 : -40, opacity: 0 }),
};

// FIX: Added 'as const' to ease to satisfy Framer Motion's strict easing type
const transition = { duration: 0.22, ease: [0.4, 0, 0.2, 1] as const };

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <motion.div
          key={i}
          animate={{
            width: i + 1 === current ? 20 : 6,
            backgroundColor: i + 1 <= current
              ? "hsl(var(--primary))"
              : "hsl(var(--border))",
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="h-1.5 rounded-full"
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

interface OnboardingWizardProps {
  isOpen: boolean;
  onClose?: () => void;
  /** Skip setup temporarily and explore the calendar first. */
  onDefer?: () => void;
}

export function OnboardingWizard({ isOpen, onClose, onDefer }: OnboardingWizardProps) {
  const intl = useIntl();
  const { profile } = useApp();
  const { completeOnboarding, uploadAvatar } = useOnboardingMutations();

  const months = useMemo(
    () => monthOptions(intl.locale),
    [intl.locale],
  );

  const stepLabels = useMemo(
    () => [
      intl.formatMessage({ id: "onboarding.step.profile" }),
      intl.formatMessage({ id: "onboarding.step.officeGoal" }),
      intl.formatMessage({ id: "onboarding.step.ai" }),
      intl.formatMessage({ id: "onboarding.step.notifications" }),
    ],
    [intl],
  );

  const goalLabel = (value: number) => {
    const index = Math.min(
      GOAL_SLIDER_KEYS.length - 1,
      Math.max(0, Math.round(value / 10)),
    );
    return intl.formatMessage({ id: GOAL_SLIDER_KEYS[index] });
  };

  const aiFeatures = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "onboarding.ai.pattern" }),
        desc: intl.formatMessage({ id: "onboarding.ai.patternDesc" }),
      },
      {
        label: intl.formatMessage({ id: "onboarding.ai.smart" }),
        desc: intl.formatMessage({ id: "onboarding.ai.smartDesc" }),
      },
      {
        label: intl.formatMessage({ id: "onboarding.ai.goal" }),
        desc: intl.formatMessage({ id: "onboarding.ai.goalDesc" }),
      },
    ],
    [intl],
  );

  const notificationItems = useMemo(
    () => [
      {
        key: "pushEnabled" as const,
        label: intl.formatMessage({ id: "onboarding.notif.smartReminder" }),
        desc: intl.formatMessage({ id: "onboarding.notif.smartReminderDesc" }),
      },
      {
        key: "emailEnabled" as const,
        label: intl.formatMessage({ id: "onboarding.notif.digest" }),
        desc: intl.formatMessage({ id: "onboarding.notif.digestDesc" }),
      },
    ],
    [intl],
  );

  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [closing, setClosing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState<OnboardingPayload>({
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    birthday: profile?.birthday ?? null,
    avatarUrl: profile?.avatarUrl || null,
    officeGoal: 50,
    officeGoalType: "percent",
    officeGoalPeriod: "month",
    yearType: "natural",
    fiscalStartMonth: 1,
    aiEnabled: true,
    pushEnabled: true,
    emailEnabled: true,
  });

  // Populate form fields when profile data becomes available
  React.useEffect(() => {
    if (profile) {
      setForm(prev => ({
        ...prev,
        firstName: prev.firstName || profile.firstName || "",
        lastName: prev.lastName || profile.lastName || "",
        birthday: prev.birthday || profile.birthday || null,
        avatarUrl: prev.avatarUrl || profile.avatarUrl || null,
      }));
    }
  }, [profile]);

  // ── Birthday helpers ────────────────────────────────────────────────────
  const [bdMonth, bdDay] = form.birthday
    ? form.birthday.split("-")
    : ["", ""];

  const daysInSelectedMonth = bdMonth
    ? new Date(2024, parseInt(bdMonth), 0).getDate()
    : 31;

  const setBirthday = (month: string, day: string) =>
    setForm(f => ({ ...f, birthday: `${month}-${day}` }));

  // ── Navigation ──────────────────────────────────────────────────────────
  const next = () => {
    if (step < 4) { setDir(1); setStep(s => s + 1); }
    else { handleComplete(); }
  };

  const prev = () => {
    if (step > 1) { setDir(-1); setStep(s => s - 1); }
  };

  const handleComplete = async () => {
    setClosing(true);
    try {
      await completeOnboarding.mutateAsync(form);
      onClose?.();
    } catch {
      setClosing(false);
    }
  };

  // ── Avatar upload ────────────────────────────────────────────────────────
  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.userId) return;
    setUploading(true);
    try {
      const url = await uploadAvatar.mutateAsync({ file, userId: profile.userId });
      setForm(f => ({ ...f, avatarUrl: url }));
    } finally {
      setUploading(false);
    }
  };

  // ── Goal period change ───────────────────────────────────────────────────
  const handleGoalPeriod = (period: "week" | "month" | "quarter") => {
    const isWeekly = period === "week";
    setForm(f => ({
      ...f,
      officeGoalPeriod: period,
      officeGoalType: isWeekly ? "days" : "percent",
      officeGoal: isWeekly ? 3 : 50,
    }));
  };

  // ── Step validation ──────────────────────────────────────────────────────
  const isValid = () => {
    if (step === 1) {
      return (
        form.firstName.trim().length > 0 &&
        form.lastName.trim().length > 0
      );
    }
    return true;
  };

  // ── Initials for avatar fallback ─────────────────────────────────────────
  const initials =
    (form.firstName?.[0] ?? "") + (form.lastName?.[0] ?? "");

  const isPending = completeOnboarding.isPending;

  return (
    <Dialog
      open={isOpen && !closing}
      onOpenChange={(open) => {
        // Prevent close if user hasn't onboarded yet
        if (!open && !profile?.isOnboarded) return;
        onClose?.();
      }}
    >
      <DialogContent className="sm:max-w-[460px] p-0 overflow-hidden rounded-3xl border border-border/60 shadow-2xl bg-background gap-0 [&>button]:hidden">
        
        <DialogTitle className="sr-only">
          {intl.formatMessage({ id: "onboarding.wizard.title" })}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {intl.formatMessage({ id: "onboarding.wizard.description" })}
        </DialogDescription>

        {/* ── Progress bar ── */}
        <div className="relative h-[2px] bg-border/40 w-full overflow-hidden">
          <motion.div
            className="absolute h-full bg-primary"
            animate={{ width: `${(step / 4) * 100}%` }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          />
        </div>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-8 pt-8 pb-0">
          <StepDots current={step} total={4} />
          <span className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase">
            {step} / 4
          </span>
        </div>

        {/* ── Step content ── */}
        <div className="relative overflow-hidden">
          <AnimatePresence initial={false} custom={dir} mode="wait">
            <motion.div
              key={step}
              custom={dir}
              variants={slide}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="px-8 pt-6 pb-2"
            >
              {/* Step title */}
              <div className="mb-7">
                <p className="text-xs font-semibold text-primary/70 uppercase tracking-widest mb-1">
                  {stepLabels[step - 1]}
                </p>
                <h2 className="text-2xl font-bold tracking-tight text-foreground leading-none">
                  {step === 1 && intl.formatMessage({ id: "onboarding.step1.title" })}
                  {step === 2 && intl.formatMessage({ id: "onboarding.step2.title" })}
                  {step === 3 && intl.formatMessage({ id: "onboarding.step3.title" })}
                  {step === 4 && intl.formatMessage({ id: "onboarding.step4.title" })}
                </h2>
              </div>

              {/* ── STEP 1: Profile ── */}
              {step === 1 && (
                <div className="space-y-5">
                  {/* Avatar */}
                  <div className="flex justify-center mb-2">
                    <div className="relative group">
                      <Avatar className="h-20 w-20 ring-2 ring-border shadow-md relative overflow-hidden">
                        <AvatarImage src={form.avatarUrl ?? ""} className="object-cover" />
                        <AvatarFallback className="bg-muted text-muted-foreground text-lg font-semibold">
                          {initials || "?"}
                        </AvatarFallback>
                        
                        {/* Elegant loading overlay */}
                        <AnimatePresence>
                          {uploading && (
                            <motion.div 
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="absolute inset-0 bg-background/40 backdrop-blur-[2px] flex items-center justify-center"
                            >
                              <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Avatar>
                      
                      {/* Pulse effect ring */}
                      <AnimatePresence>
                        {uploading && (
                          <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1.2, opacity: [0, 0.5, 0] }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                            className="absolute inset-0 rounded-full border-2 border-primary pointer-events-none"
                          />
                        )}
                      </AnimatePresence>

                      <Label
                        htmlFor="avatar-upload"
                        className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-pointer shadow-lg hover:scale-105 transition-transform"
                      >
                        {uploading
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Camera className="h-3.5 w-3.5" />}
                      </Label>
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFile}
                        disabled={uploading}
                      />
                    </div>
                  </div>

                  {/* Name */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        {intl.formatMessage({ id: "onboarding.firstName" })}
                      </Label>
                      <Input
                        placeholder={intl.formatMessage({ id: "onboarding.placeholder.firstName" })}
                        value={form.firstName}
                        onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                        className="h-10 bg-muted/30 border-muted/60"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        {intl.formatMessage({ id: "onboarding.lastName" })}
                      </Label>
                      <Input
                        placeholder={intl.formatMessage({ id: "onboarding.placeholder.lastName" })}
                        value={form.lastName}
                        onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                        className="h-10 bg-muted/30 border-muted/60"
                      />
                    </div>
                  </div>

                  {/* Birthday */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      {intl.formatMessage({ id: "onboarding.birthday" })}
                    </Label>
                    <div className="flex gap-2">
                      <Select
                        value={bdMonth}
                        onValueChange={m => setBirthday(m, bdDay || "01")}
                      >
                        <SelectTrigger className="flex-1 h-10 bg-muted/30 border-muted/60">
                          <SelectValue placeholder={intl.formatMessage({ id: "onboarding.month" })} />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={bdDay}
                        onValueChange={d => setBirthday(bdMonth || "01", d)}
                        disabled={!bdMonth}
                      >
                        <SelectTrigger className="w-[90px] h-10 bg-muted/30 border-muted/60">
                          <SelectValue placeholder={intl.formatMessage({ id: "onboarding.day" })} />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: daysInSelectedMonth }, (_, i) => {
                            const d = String(i + 1).padStart(2, "0");
                            return <SelectItem key={d} value={d}>{d}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* ── STEP 2: Office Goal ── */}
              {step === 2 && (
                <div className="space-y-6">
                  {/* Period selector */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      {intl.formatMessage({ id: "onboarding.goalPeriod" })}
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(["week", "month", "quarter"] as const).map(p => (
                        <button
                          key={p}
                          onClick={() => handleGoalPeriod(p)}
                          className={cn(
                            "py-2.5 rounded-xl text-sm font-medium border transition-all duration-150 capitalize",
                            form.officeGoalPeriod === p
                              ? "bg-primary text-primary-foreground border-primary shadow-sm"
                              : "bg-muted/30 border-muted/60 text-muted-foreground hover:border-muted-foreground/30"
                          )}
                        >
                          {p === "week"
                            ? intl.formatMessage({ id: "onboarding.period.weekly" })
                            : p === "month"
                              ? intl.formatMessage({ id: "onboarding.period.monthly" })
                              : intl.formatMessage({ id: "onboarding.period.quarterly" })}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Goal value */}
                  {form.officeGoalType === "percent" ? (
                    <div className="space-y-4">
                      <div className="flex items-end justify-between">
                        <Label className="text-xs text-muted-foreground">
                          {intl.formatMessage({ id: "onboarding.officePresenceTarget" })}
                        </Label>
                        <div className="text-right">
                          <span className="text-3xl font-semibold text-foreground tabular-nums">{form.officeGoal}</span>
                          <span className="text-lg font-bold text-primary/70">%</span>
                        </div>
                      </div>
                      <Slider
                        value={[form.officeGoal]}
                        onValueChange={([v]) => setForm(f => ({ ...f, officeGoal: v }))}
                        max={100} step={5}
                        className="py-1"
                      />
                      <p className="text-xs text-muted-foreground text-center">
                        {goalLabel(Math.round(form.officeGoal / 10) * 10)}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Label className="text-xs text-muted-foreground">
                        {intl.formatMessage({ id: "onboarding.daysPerWeek" })}
                      </Label>
                      <div className="flex justify-center">
                        <ToggleGroup
                          type="single"
                          value={String(form.officeGoal)}
                          onValueChange={v => v && setForm(f => ({ ...f, officeGoal: parseInt(v) }))}
                          className="bg-muted/30 p-1.5 rounded-2xl border border-muted/50 gap-1.5"
                        >
                          {[1, 2, 3, 4, 5].map(d => (
                            <ToggleGroupItem
                              key={d}
                              value={String(d)}
                              className="h-11 w-11 rounded-xl text-base font-bold data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                            >
                              {d}
                            </ToggleGroupItem>
                          ))}
                        </ToggleGroup>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        {intl.formatMessage(
                          { id: "onboarding.daysPerWeekLabel" },
                          { count: form.officeGoal },
                        )}
                      </p>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="h-px bg-border/50" />

                  {/* Year type */}
                  <div className="space-y-3">
                    <Label className="text-xs text-muted-foreground">
                      {intl.formatMessage({ id: "onboarding.calendarYear" })}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {(["natural", "fiscal"] as const).map(t => (
                        <button
                          key={t}
                          onClick={() => setForm(f => ({ ...f, yearType: t }))}
                          className={cn(
                            "flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-150",
                            form.yearType === t
                              ? "bg-primary/5 border-primary text-primary shadow-sm"
                              : "bg-muted/30 border-muted/60 text-muted-foreground hover:border-muted-foreground/30"
                          )}
                        >
                          <Calendar className="h-4 w-4 flex-shrink-0" />
                          <span>
                            {t === "natural"
                              ? intl.formatMessage({ id: "onboarding.naturalYear" })
                              : intl.formatMessage({ id: "onboarding.fiscalYear" })}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Fiscal month — only when fiscal selected */}
                    <AnimatePresence>
                      {form.yearType === "fiscal" && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-1 space-y-1.5">
                            <Label className="text-xs text-muted-foreground">
                              {intl.formatMessage({ id: "onboarding.fiscalStartsIn" })}
                            </Label>
                            <Select
                              value={form.fiscalStartMonth.toString()}
                              onValueChange={v => setForm(f => ({ ...f, fiscalStartMonth: parseInt(v) }))}
                            >
                              <SelectTrigger className="h-10 bg-muted/30 border-muted/60">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {months.map(m => (
                                  <SelectItem key={m.value} value={String(parseInt(m.value))}>
                                    {m.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {/* ── STEP 3: AI ── */}
              {step === 3 && (
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-4 p-5 rounded-2xl border bg-muted/20">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold">
                        {intl.formatMessage({ id: "onboarding.ai.title" })}
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {intl.formatMessage({ id: "onboarding.ai.description" })}
                      </p>
                    </div>
                    <Switch
                      checked={form.aiEnabled}
                      onCheckedChange={v => setForm(f => ({ ...f, aiEnabled: v }))}
                      className="flex-shrink-0 mt-0.5"
                    />
                  </div>

                  <div className="space-y-3">
                    {aiFeatures.map(f => (
                      <div key={f.label} className="flex items-start gap-3">
                        <div className={cn(
                          "h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors",
                          form.aiEnabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground/40"
                        )}>
                          <Sparkles className="h-3 w-3" />
                        </div>
                        <div>
                          <p className={cn("text-sm font-medium transition-colors", !form.aiEnabled && "text-muted-foreground/60")}>{f.label}</p>
                          <p className="text-xs text-muted-foreground">{f.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-[11px] text-muted-foreground/60 text-center">
                    {intl.formatMessage({ id: "onboarding.ai.settingsNote" })}
                  </p>
                </div>
              )}

              {/* ── STEP 4: Notifications ── */}
              {step === 4 && (
                <div className="space-y-3">
                  {notificationItems.map(item => (
                    <div
                      key={item.key}
                      className={cn(
                        "flex items-center justify-between gap-4 p-4 rounded-2xl border transition-all duration-150",
                        form[item.key]
                          ? "bg-primary/5 border-primary/30"
                          : "bg-muted/20 border-muted/60"
                      )}
                    >
                      <div className="space-y-0.5">
                        <p className="text-sm font-semibold">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                      <Switch
                        checked={form[item.key]}
                        onCheckedChange={v => setForm(f => ({ ...f, [item.key]: v }))}
                        className="flex-shrink-0"
                      />
                    </div>
                  ))}

                  <div className="pt-4 rounded-2xl bg-muted/10 p-4 text-center space-y-1">
                    <p className="text-sm font-semibold text-foreground">
                      {intl.formatMessage({ id: "onboarding.allSet.title" })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {intl.formatMessage({ id: "onboarding.allSet.description" })}
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── Footer ── */}
        <div className="px-8 py-6 space-y-3">
          {step === 1 && onDefer && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={onDefer}
                disabled={isPending || uploading}
                className="text-xs text-muted-foreground hover:text-foreground
                  transition-colors underline-offset-2 hover:underline disabled:opacity-50"
              >
                {intl.formatMessage({ id: "onboarding.exploreFirst" })}
              </button>
            </div>
          )}
          <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={prev}
            disabled={step === 1 || isPending}
            className="text-muted-foreground hover:text-foreground rounded-xl gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            {intl.formatMessage({ id: "onboarding.back" })}
          </Button>

          <Button
            onClick={next}
            disabled={!isValid() || isPending || uploading}
            className="rounded-xl px-6 min-w-[130px] font-semibold gap-1.5 shadow-sm"
          >
            {isPending ? (
              <><Loader2 className="h-4 w-4 animate-spin" />{intl.formatMessage({ id: "onboarding.saving" })}</>
            ) : step === 4 ? (
              <><Check className="h-4 w-4" />{intl.formatMessage({ id: "onboarding.getStarted" })}</>
            ) : (
              <>{intl.formatMessage({ id: "onboarding.continue" })}<ChevronRight className="h-4 w-4" /></>
            )}
          </Button>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}
